import React from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { Baby, GraduationCap, ShieldCheck, Sparkles, ArrowRight, Store, Wallet, HeartHandshake, Users } from "lucide-react";

export default function AppPortal() {
  const { user } = useAuth();
  // If a parent is already signed in, the Parent Zone card deep-links straight
  // to their dashboard. Otherwise it sends them to sign-in.
  const parentHref = user ? "/dashboard/parent" : "/auth?mode=login";
  const parentCta = user ? "Open my dashboard" : "Sign in as parent";

  return (
    <div className="min-h-screen bg-[#FFFDF7]" data-testid="app-portal">
      <Navbar />

      <section className="max-w-6xl mx-auto px-6 lg:px-8 pt-14 lg:pt-20 pb-8">
        <div className="pill bg-brandPink/15 text-brandPink" data-testid="portal-eyebrow">
          <Sparkles className="w-3.5 h-3.5 mr-1.5" /> App portal
        </div>
        <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-brandSlate leading-[1.05] mt-4" data-testid="portal-title">
          Who&rsquo;s using MyTableTopApp today?
        </h1>
        <p className="text-lg text-slate-600 mt-4 max-w-2xl leading-relaxed" data-testid="portal-subtitle">
          Pick your zone. Parents keep the keys; kids get their own guided shopfront experience.
        </p>
      </section>

      {/* Two-card selection grid */}
      <section className="max-w-6xl mx-auto px-6 lg:px-8 pb-16 grid md:grid-cols-2 gap-6" data-testid="portal-cards">
        {/* Kid & Teen Zone */}
        <div
          className="relative card-base overflow-hidden group hover:-translate-y-1 hover:shadow-pop transition-all"
          data-testid="portal-card-kid"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-brandPink/15 via-white to-brandPink/5 pointer-events-none" />
          <div className="relative p-8 md:p-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brandPink text-white shadow-soft">
              <Baby className="w-8 h-8" />
            </div>
            <div className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-brandPink">Kid &amp; Teen Zone</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-brandSlate mt-1">Explore the shopfront</h2>
            <p className="text-slate-600 mt-3 leading-relaxed">
              Browse toys other kids are selling, list your own with a grown-up&rsquo;s approval, and see what you&rsquo;ve earned.
              Everything here is safety-first — preset messaging, no free chat.
            </p>

            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2"><Store className="w-4 h-4 text-brandPink mt-0.5" /> Browse and search — no login needed</li>
              <li className="flex items-start gap-2"><GraduationCap className="w-4 h-4 text-brandPink mt-0.5" /> List your own toy (parent approves before it goes live)</li>
              <li className="flex items-start gap-2"><Wallet className="w-4 h-4 text-brandPink mt-0.5" /> See sales, earnings, and your seller rating</li>
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/browse" className="btn-primary" data-testid="portal-kid-browse">
                Browse the marketplace <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/auth?mode=login" className="btn-secondary" data-testid="portal-kid-signin">
                Sign in
              </Link>
            </div>
          </div>
        </div>

        {/* Parent Zone */}
        <div
          className="relative card-base overflow-hidden group hover:-translate-y-1 hover:shadow-pop transition-all"
          data-testid="portal-card-parent"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-brandTeal/15 via-white to-brandTeal/5 pointer-events-none" />
          <div className="relative p-8 md:p-10">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-brandTeal text-white shadow-soft">
              <ShieldCheck className="w-8 h-8" />
            </div>
            <div className="mt-6 text-xs font-bold uppercase tracking-[0.15em] text-brandTeal">Parent Zone</div>
            <h2 className="font-display text-3xl md:text-4xl font-semibold text-brandSlate mt-1">The control room</h2>
            <p className="text-slate-600 mt-3 leading-relaxed">
              Approve every listing before it goes live, track earnings across all your kids&rsquo; profiles,
              and manage payouts, safety PIN, and independent banking toggles.
            </p>

            <ul className="mt-6 space-y-2 text-sm text-slate-700">
              <li className="flex items-start gap-2"><HeartHandshake className="w-4 h-4 text-brandTeal mt-0.5" /> Moderation queue for every kid listing</li>
              <li className="flex items-start gap-2"><Users className="w-4 h-4 text-brandTeal mt-0.5" /> Manage multiple kid profiles from one account</li>
              <li className="flex items-start gap-2"><Wallet className="w-4 h-4 text-brandTeal mt-0.5" /> Family wallet, fees breakdown, Stripe payouts</li>
            </ul>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to={parentHref} className="btn-teal" data-testid="portal-parent-primary">
                {parentCta} <ArrowRight className="w-4 h-4" />
              </Link>
              {!user && (
                <Link to="/auth?mode=register" className="btn-secondary" data-testid="portal-parent-register">
                  Create parent account
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
